import {useDispatch} from 'react-redux'
import {Hotel, Room} from '@findhotel/sapi'

import {
  trackEvent,
  trackProviderRedirect
} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity,
  Page,
  Team
} from '@vio/core/src/analytics/types/Events'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'

import {useCreateAnalyticsLeadContext} from '../components/DealFreezeButton/useCreateAnalyticsLeadContext'
import {
  DealFreezeAnalyticsContext,
  openFreezeOverlay,
  setAnalyticsContext
} from '../modules/slice'
import {clearFreezeIntent, readFreezeIntent} from '../utils/freezeIntent'
import {
  describeResumeMatch,
  findExactMatchedOffer,
  findFallbackMatchedOffer,
  findRoomByOfferId,
  isFreezeBoardEquivalent
} from '../utils/offer'
import {isOfferEligibleForDealFreeze} from '../utils/OfferEligibility'
import {clearDealFreezeStash, readDealFreezeStash} from '../utils/stash'

// resumed = populated; gone = offer no longer freezable (stash cleared);
// not-ready = no stash or rooms not loaded yet, caller re-invokes later.
export type ResumeOutcome = 'resumed' | 'gone' | 'not-ready'

type Params = {
  /** AP-side rooms data. Enables availability checking + room hydration for stashes that omit `room`. */
  rooms?: Room[]
  /**
   * Authoritative analytics context derived from AP state for the stashed offerId.
   * When provided, this is used instead of any stashed `analyticsContext` — AP has
   * the freshest offer/price/lead/hotel context, which is more useful for analysis
   * than what SRP would stash.
   */
  analyticsContextOverride?: DealFreezeAnalyticsContext
}

export const useResumableDealFreeze = ({
  rooms,
  analyticsContextOverride
}: Params = {}) => {
  const dispatch = useDispatch()
  const isHydrated = useIsHydrated()

  const DF_SELECTED_OFFER = isHydrated ? readDealFreezeStash() : null

  const leadContextRequiredData = DF_SELECTED_OFFER
    ? DF_SELECTED_OFFER.leadContextRequiredData
    : {}
  const createAnalyticsLeadContext = useCreateAnalyticsLeadContext({
    ...leadContextRequiredData,
    trackProviderRedirect,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - the stash is unreadable on the server, so offerId is undefined initially
    offerId: DF_SELECTED_OFFER?.offerId
  })

  const resumeDealFreeze = (): ResumeOutcome => {
    if (!DF_SELECTED_OFFER) return 'not-ready'

    // Distinguish "rooms not loaded yet" (empty array during a render) from
    // "rooms loaded but offer is gone" — only the latter should clear the
    // stash. Treating empty as "gone" silently kills the SRP-exit-popup resume
    // flow when the effect fires before rooms data is in the selector.
    if (!Array.isArray(rooms) || rooms.length === 0) {
      // Rooms not populated yet — leave stash in place. The caller is
      // responsible for re-invoking resume once rooms data arrives.
      return 'not-ready'
    }

    // Availability check: verify the stashed offer still exists in current AP
    // rooms data AND is still freezable. Handles offer-gone-by-AP-load (sold
    // out, provider dropped it, deal_freeze tag removed).
    //
    // Post-auth searches regenerate SAPI offer ids, so the exact match misses
    const exactOffer = findExactMatchedOffer(rooms, DF_SELECTED_OFFER.offerId)
    const matchedOffer =
      exactOffer ??
      findFallbackMatchedOffer(
        rooms,
        DF_SELECTED_OFFER.roomOffer,
        DF_SELECTED_OFFER.room?.id
      )
    if (!matchedOffer || !isOfferEligibleForDealFreeze(matchedOffer.tags)) {
      // Offer no longer freezable: clear stash + intent so nothing auto-freezes.
      clearDealFreezeStash()
      clearFreezeIntent()
      return 'gone'
    }

    clearDealFreezeStash()

    // Auto-freeze only when a committed intent matches and the fresh match is
    // board-equivalent to the stashed offer (a DealFreeze is always penalty-free
    // and equal-or-cheaper); otherwise the user confirms.
    const intent = readFreezeIntent()
    const intentMatches =
      !!intent &&
      intent.committed &&
      intent.hotelId === (DF_SELECTED_OFFER.hotel as Hotel)?.objectID
    const boardEquivalent = isFreezeBoardEquivalent(
      DF_SELECTED_OFFER.roomOffer,
      matchedOffer
    )
    const shouldAutoFreeze = intentMatches && boardEquivalent
    const resumeOutcome = intentMatches
      ? boardEquivalent
        ? ('autoFreeze' as const)
        : ('confirmation' as const)
      : undefined
    clearFreezeIntent()

    // Prefer the AP-derived analytics context when provided; AP has rich,
    // current contexts that beat anything SRP could have stashed.
    const analyticsContext =
      analyticsContextOverride ?? DF_SELECTED_OFFER.analyticsContext ?? {}
    dispatch(setAnalyticsContext(analyticsContext))

    // The openFreezeOverlay reducer resolves sapiMatchedOffer by finding the
    // offer id inside this room, so it must be the fresh AP room.
    const room = findRoomByOfferId(rooms, matchedOffer.id)
    dispatch(
      openFreezeOverlay({
        offer: {
          id: matchedOffer.id,
          providerCode: matchedOffer.providerCode,
          links: matchedOffer.links
        },
        hotel: DF_SELECTED_OFFER.hotel as Hotel,
        searchDetail: DF_SELECTED_OFFER.searchDetail,
        room: room && [room as Room],
        component: DF_SELECTED_OFFER.component,
        shouldAutoFreeze,
        resumeOutcome
      })
    )

    // A fallback can swap the rate, and later events report the stashed offer.
    dispatch(
      trackEvent({
        category: Category.System,
        entity: Entity.DealFreeze,
        action: Action.Resumed,
        page: Page.Accommodation,
        ...(DF_SELECTED_OFFER.component && {
          component: DF_SELECTED_OFFER.component
        }),
        team: Team.Select,
        analyticsContext: {
          ...(analyticsContext.offerContext && {
            [AnalyticsContext.OfferContext]: analyticsContext.offerContext
          }),
          ...(analyticsContext.priceContext && {
            [AnalyticsContext.PriceContext]: analyticsContext.priceContext
          })
        },
        payload: {
          ...describeResumeMatch(
            exactOffer ? 'exact' : 'fallback',
            DF_SELECTED_OFFER.roomOffer,
            matchedOffer
          ),
          ...(resumeOutcome && {resumeOutcome})
        }
      })
    )
    createAnalyticsLeadContext()
    return 'resumed'
  }

  // Use this to prevent race-conditions with the parsing logic above
  const resumableDealFreezeReady = !!DF_SELECTED_OFFER

  // Lets the caller seed the skeleton + failure card before offers finish polling.
  const stashedHotel = DF_SELECTED_OFFER?.hotel ?? null
  const stashedSearchDetail = DF_SELECTED_OFFER?.searchDetail ?? null

  return {
    resumableDealFreezeReady,
    resumeDealFreeze,
    stashedHotel,
    stashedSearchDetail
  }
}
